import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { format, subDays } from 'date-fns';
import PageLayout from '../../layouts/PageLayout';
import { useAuth } from '../../context/AuthProvider';
import { useBranch } from '../../context/BranchContext';
import { attendanceAPI, cashVoucherAPI, cashierAPI, shiftAPI } from '../../services';
import { toast } from 'react-toastify';

import ShiftMonitorTable from '../../components/cashier/ShiftMonitorTable';
import VoucherList from '../../components/cashier/VoucherList';
import VoucherStatsCard from '../../components/cashier/VoucherStatsCard';
import VoucherDetailModal from '../../components/cashier/VoucherDetailModal';
import RejectVoucherModal from '../../components/cashier/RejectVoucherModal';
import CancelVoucherModal from '../../components/cashier/CancelVoucherModal';
import BackdatedVoucherModal from '../../components/cashier/BackdatedVoucherModal';
import ForceCheckoutModal from '../../components/cashier/ForceCheckoutModal';
import CashierReportModal from '../../components/cashier/CashierReportModal';
import useFilterParams from '../../hooks/useFilterParams';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';

const formatCurrency = (value) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(
    Number(value || 0),
  );

const getDefaultFilters = () => {
  const today = new Date();
  return {
    status: 'APPROVED',
    date_from: format(subDays(today, 6), 'yyyy-MM-dd'),
    date_to: format(today, 'yyyy-MM-dd'),
    search: '',
  };
};

const normalizeShift = (shift) => {
  if (!shift) return null;
  const cashSummary = shift.cash_summary || shift.cashSummary || {};
  const expected = Number(
    cashSummary.expected_closing_cash ?? cashSummary.expectedClosing ?? shift.expected_closing_cash ?? 0,
  );
  const actual = Number(
    cashSummary.actual_closing_cash ?? cashSummary.actualClosing ?? shift.actual_closing_cash ?? shift.closing_cash ?? 0,
  );
  const opening = Number(shift.opening_cash ?? cashSummary.opening_cash ?? 0);
  const revenue = Number(cashSummary.cash_revenue ?? shift.cash_revenue ?? 0);
  const expenses = Number(cashSummary.cash_expenses ?? shift.cash_expenses ?? 0);
  return {
    id: shift.id,
    branchName: shift.branch?.name || shift.branch_name || '-',
    employeeName: shift.employee?.full_name || shift.employee_name || '-',
    startTime: shift.start_time || shift.startTime,
    endTime: shift.end_time || shift.endTime,
    status: shift.status,
    openingCash: opening,
    closingCash: Number(shift.closing_cash ?? 0),
    expectedClosing: expected || opening + revenue - expenses,
    actualClosing: actual,
    cashRevenue: revenue,
    cashExpenses: expenses,
    difference: Number(cashSummary.difference ?? actual - (expected || opening + revenue - expenses)),
  };
};

const normalizeVoucher = (v) => {
  if (!v) return null;
  return {
    id: v.id,
    voucherNumber: v.voucher_number || v.voucherNumber,
    branchName: v.branch?.name || v.branch_name || '-',
    amount: Number(v.amount || 0),
    category: v.category,
    paymentMethod: v.payment_method,
    status: v.status,
    description: v.description,
    recipientName: v.recipient_name,
    shiftId: v.shift_id,
    createdAt: v.created_at || v.createdAt,
    // #235631: ngày CHI và người đề nghị/duyệt đưa thẳng ra bảng, khỏi phải mở
    // modal từng phiếu mới biết ai chi cái gì.
    voucherDate: v.voucher_date || v.voucherDate,
    createdByName: v.creator?.full_name || v.created_by_name,
    approvedByName: v.approver?.full_name || v.approved_by_name,
    approvedAt: v.approved_at || v.approvedAt,
    // #164550 — huỷ phiếu chi đã duyệt.
    cancelledByName: v.canceller?.full_name || v.cancelled_by_name,
    cancelledAt: v.cancelled_at || v.cancelledAt,
    cancelReason: v.cancel_reason || v.cancelReason,
    // #110729 — phiếu chi LÙI CA (không trừ két).
    isBackdated: Boolean(v.is_backdated),
    varianceRefId: v.variance_ref_id || null,
  };
};

const extractError = (error) =>
  error?.response?.data?.message || error?.message || 'Thao tác thất bại';

/**
 * #191439 — Thẻ "Phiếu chi" của màn này là ĐÍCH của link sâu trong tin phiếu chi chờ duyệt
 * (dòng 181 của bảng khảo sát #132320). Tin gửi `?branch_id=&q=<số phiếu>&status=PENDING`.
 *
 * Trước đợt này tin đó gửi link `/admin/cashier-management` — một đường dẫn KHÔNG TỒN TẠI
 * (tuyến thật là `/cashier-management`, `src/index.js:124`) nên bấm vào luôn ra trang lỗi.
 * Xem chú thích cùng mã việc ở `on-coffee-server/src/modules/cash-voucher/cash-voucher.service.ts`.
 *
 * `q` khớp thẳng tham số `search` của API phiếu chi — không phải lọc phía trình duyệt.
 */
const CASHIER_MANAGEMENT_FILTER_SPEC = {
  q: { default: '', alias: ['search'], debounce: 350 },
  status: {
    default: 'PENDING',
    parse: (v) => v.toUpperCase(),
    valid: (v) =>
      ['', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'].includes(v.toUpperCase()),
  },
};

const CashierManagementPage = () => {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();
  const branchId = selectedBranch?.id;

  // ─── Shift state ───────────────────────────────────────────────────────────
  const [filters, setFilters] = useState(getDefaultFilters);
  const [shifts, setShifts] = useState([]);
  const [shiftMeta, setShiftMeta] = useState(null);
  const [shiftLoading, setShiftLoading] = useState(false);
  const [shiftPage, setShiftPage] = useState(1);
  const shiftLimit = 10;

  // ─── Voucher state ─────────────────────────────────────────────────────────
  const [voucherUrlFilters, setVoucherUrlFilters, , { ignored, dismissIgnored }] =
    useFilterParams(CASHIER_MANAGEMENT_FILTER_SPEC);
  const voucherStatusFilter = voucherUrlFilters.status;
  const voucherSearch = voucherUrlFilters.q;
  const setVoucherStatusFilter = (v) => setVoucherUrlFilters({ status: v });
  const setVoucherSearch = (v) => setVoucherUrlFilters({ q: v });
  // #235631: phiếu chi có bộ lọc ngày RIÊNG. Trước đây nó dùng ké ô ngày của thẻ
  // "Giám sát ca thu ngân" nên người xem không biết ngày nào đang được áp.
  const [voucherDateFrom, setVoucherDateFrom] = useState(() => getDefaultFilters().date_from);
  const [voucherDateTo, setVoucherDateTo] = useState(() => getDefaultFilters().date_to);
  const [vouchers, setVouchers] = useState([]);
  const [voucherMeta, setVoucherMeta] = useState(null);
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [voucherStats, setVoucherStats] = useState(null);
  const [voucherPage, setVoucherPage] = useState(1);
  const voucherLimit = 10;

  // ─── Modal state ───────────────────────────────────────────────────────────
  const [detailVoucherId, setDetailVoucherId] = useState(null);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectLoading, setRejectLoading] = useState(false);

  // #164550 — huỷ phiếu chi đã duyệt (chỉ ADMIN).
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // #110729 — tạo phiếu chi LÙI CA (chỉ ADMIN).
  const [backdatedOpen, setBackdatedOpen] = useState(false);
  const [backdatedLoading, setBackdatedLoading] = useState(false);

  const [forceTarget, setForceTarget] = useState(null);
  const [forceLoading, setForceLoading] = useState(false);

  const [reportModal, setReportModal] = useState(false);
  const [reportShift, setReportShift] = useState(null);
  const [cashierReport, setCashierReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  // ─── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchShifts = useCallback(async () => {
    try {
      setShiftLoading(true);
      const response = await shiftAPI.getShifts({
        branch_id: branchId,
        status: filters.status || undefined,
        date_from: filters.date_from,
        date_to: filters.date_to,
        search: filters.search || undefined,
        is_cashier: true,
        page: shiftPage,
        limit: shiftLimit,
      });
      const items = Array.isArray(response?.data) ? response.data : [];
      setShifts(items.map(normalizeShift).filter(Boolean));
      setShiftMeta(response?.meta || null);
    } catch (error) {
      toast.error(`Không tải được danh sách ca: ${extractError(error)}`);
    } finally {
      setShiftLoading(false);
    }
  }, [branchId, filters, shiftPage]);

  const fetchVoucherStats = useCallback(async () => {
    try {
      const response = await cashVoucherAPI.getCashVoucherStats({
        branch_id: branchId,
        from_date: voucherDateFrom,
        to_date: voucherDateTo,
      });
      setVoucherStats(response);
    } catch {
      // stats không hiện critical, bỏ qua
    }
  }, [branchId, voucherDateFrom, voucherDateTo]);

  const fetchVouchers = useCallback(async () => {
    try {
      setVoucherLoading(true);
      const response = await cashVoucherAPI.getCashVouchers({
        branch_id: branchId,
        status: voucherStatusFilter || undefined,
        search: voucherSearch || undefined,
        page: voucherPage,
        limit: voucherLimit,
        from_date: voucherDateFrom,
        to_date: voucherDateTo,
      });
      const items = Array.isArray(response?.data) ? response.data : [];
      setVouchers(items.map(normalizeVoucher).filter(Boolean));
      setVoucherMeta(response?.meta || null);
    } catch (error) {
      toast.error(`Không tải được phiếu chi: ${extractError(error)}`);
    } finally {
      setVoucherLoading(false);
    }
  }, [branchId, voucherDateFrom, voucherDateTo, voucherPage, voucherSearch, voucherStatusFilter]);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);
  useEffect(() => { fetchVouchers(); fetchVoucherStats(); }, [fetchVouchers, fetchVoucherStats]);

  // ─── Summary (dùng backend stats, fallback sang compute từ page) ──────────
  const summary = useMemo(() => {
    if (voucherStats) {
      return {
        totalVouchers: voucherStats.total_count || 0,
        totalVoucherAmount: voucherStats.total_amount || 0,
      };
    }
    return { totalVouchers: 0, totalVoucherAmount: 0 };
  }, [voucherStats]);

  // #235631: tổng tiền ĐÚNG theo trạng thái đang lọc (lấy từ thống kê của backend).
  // Bỏ qua khi đang gõ tìm kiếm vì thống kê không biết từ khoá — thà không hiện còn
  // hơn hiện một con số không khớp danh sách bên cạnh.
  const filteredAmount = useMemo(() => {
    if (!voucherStats || voucherSearch) return null;
    if (!voucherStatusFilter) return voucherStats.total_amount || 0;
    const row = (voucherStats.by_status || []).find((i) => i.status === voucherStatusFilter);
    return row ? row.amount : 0;
  }, [voucherStats, voucherStatusFilter, voucherSearch]);

  const shiftSummary = useMemo(() => ({
    total: shiftMeta?.total || shifts.length,
    totalRevenue: shifts.reduce((s, sh) => s + (sh.cashRevenue || 0), 0),
    totalExpenses: shifts.reduce((s, sh) => s + (sh.cashExpenses || 0), 0),
    totalDifference: shifts.reduce((s, sh) => s + (sh.difference || 0), 0),
  }), [shifts, shiftMeta]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setShiftPage(1);
  };

  const openCashierReport = async (shift) => {
    setReportShift(shift);
    setReportModal(true);
    setCashierReport(null);
    setReportLoading(true);
    try {
      const response = await cashierAPI.getCashierReport(shift.id);
      setCashierReport(response);
    } catch (error) {
      toast.error(`Không tải được báo cáo: ${extractError(error)}`);
    } finally {
      setReportLoading(false);
    }
  };

  const handleForceCheckoutConfirm = async ({ closingCash }) => {
    if (!user?.id || !forceTarget) return;
    setForceLoading(true);
    try {
      await attendanceAPI.forceCheckOut(forceTarget.id, {
        current_user_id: user.id,
        closing_cash: closingCash,
      });
      toast.success('Đã đóng ca thay');
      setForceTarget(null);
      fetchShifts();
    } catch (error) {
      toast.error(`Không thể đóng ca thay: ${extractError(error)}`);
    } finally {
      setForceLoading(false);
    }
  };

  const handleApproveVoucher = async (voucher) => {
    if (!user?.id) return toast.error('Thiếu thông tin user');
    try {
      await cashVoucherAPI.approveCashVoucher(voucher.id, user.id);
      toast.success('Đã duyệt phiếu chi');
      fetchVouchers();
      fetchVoucherStats();
    } catch (error) {
      toast.error(`Không thể duyệt phiếu: ${extractError(error)}`);
    }
  };

  const handleRejectConfirm = async (reason) => {
    if (!user?.id || !rejectTarget) return;
    setRejectLoading(true);
    try {
      await cashVoucherAPI.rejectCashVoucher(rejectTarget.id, user.id, reason);
      toast.success('Đã từ chối phiếu chi');
      setRejectTarget(null);
      fetchVouchers();
    } catch (error) {
      toast.error(`Không thể từ chối: ${extractError(error)}`);
    } finally {
      setRejectLoading(false);
    }
  };

  const handleCancelConfirm = async (reason) => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      await cashVoucherAPI.cancelCashVoucher(cancelTarget.id, reason);
      toast.success('Đã huỷ phiếu chi');
      setCancelTarget(null);
      fetchVouchers();
      fetchVoucherStats();
    } catch (error) {
      toast.error(`Không thể huỷ phiếu: ${extractError(error)}`);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleDeleteVoucher = async (voucher) => {
    if (!window.confirm(`Xóa phiếu ${voucher.voucherNumber || voucher.id}?`)) return;
    if (!user?.id) return toast.error('Thiếu thông tin user');
    try {
      await cashVoucherAPI.deleteCashVoucher(voucher.id, user.id);
      toast.success('Đã xóa phiếu chi');
      fetchVouchers();
      fetchVoucherStats();
    } catch (error) {
      toast.error(`Không thể xóa: ${extractError(error)}`);
    }
  };

  // #110729 — tạo phiếu chi lùi ca (không trừ két). Chỉ Admin thấy nút này.
  const handleCreateBackdated = async (payload) => {
    setBackdatedLoading(true);
    try {
      await cashVoucherAPI.createBackdatedVoucher(payload);
      toast.success('Đã tạo phiếu chi lùi ca (chờ duyệt)');
      setBackdatedOpen(false);
      fetchVouchers();
      fetchVoucherStats();
    } catch (error) {
      toast.error(`Không thể tạo phiếu chi lùi ca: ${extractError(error)}`);
    } finally {
      setBackdatedLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <InvalidParamsNotice
          ignored={ignored}
          onDismiss={dismissIgnored}
        />
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Quản lý thu ngân & phiếu chi</h1>
          </div>
          <div className="d-flex gap-2">
            {user?.role === 'ADMIN' && (
              <Button variant="primary" size="sm" onClick={() => setBackdatedOpen(true)}>
                + Phiếu chi lùi ca
              </Button>
            )}
            <Button variant="outline-secondary" size="sm" onClick={() => { fetchShifts(); fetchVouchers(); fetchVoucherStats(); }}>
              Làm mới
            </Button>
          </div>
        </div>

        {/* Summary bar */}
        <Row className="g-3 mb-4">
          <Col md={3}>
            <Card>
              <Card.Body>
                <small className="text-muted">Tổng ca cashier</small>
                <h4 className="mb-0">{shiftSummary.total}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Body>
                <small className="text-muted">Doanh thu (trang hiện tại)</small>
                <h4 className="mb-0">{formatCurrency(shiftSummary.totalRevenue)}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Body>
                <small className="text-muted">Tổng phiếu chi</small>
                <h4 className="mb-0">
                  {summary.totalVouchers} <small className="text-muted fs-6">phiếu</small>
                </h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Body>
                <small className="text-muted">Tổng tiền phiếu chi</small>
                <h4 className="mb-0">{formatCurrency(summary.totalVoucherAmount)}</h4>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Shift table */}
        <ShiftMonitorTable
          shifts={shifts}
          loading={shiftLoading}
          filters={filters}
          onFilterChange={handleFilterChange}
          page={shiftPage}
          limit={shiftLimit}
          totalItems={shiftMeta?.total || shiftMeta?.total_items || shifts.length}
          onPageChange={(p) => setShiftPage(p)}
          onViewReport={openCashierReport}
          onForceCheckout={(shift) => setForceTarget(shift)}
        />

        {/* Vouchers section */}
        {/* #235631: thống kê xếp ngang ở trên, bảng phiếu chi chiếm trọn bề ngang
            để nội dung chi không bị xuống dòng vụn. */}
        <Row className="g-3">
          <Col xs={12}>
            <VoucherStatsCard stats={voucherStats} />
          </Col>
          <Col xs={12}>
            <VoucherList
              vouchers={vouchers}
              loading={voucherLoading}
              statusFilter={voucherStatusFilter}
              onStatusFilterChange={(v) => { setVoucherStatusFilter(v); setVoucherPage(1); }}
              search={voucherSearch}
              onSearchChange={(v) => { setVoucherSearch(v); setVoucherPage(1); }}
              dateFrom={voucherDateFrom}
              dateTo={voucherDateTo}
              onDateFromChange={(v) => { setVoucherDateFrom(v); setVoucherPage(1); }}
              onDateToChange={(v) => { setVoucherDateTo(v); setVoucherPage(1); }}
              branchLabel={selectedBranch?.name}
              filteredAmount={filteredAmount}
              page={voucherPage}
              limit={voucherLimit}
              totalItems={voucherMeta?.total}
              onPageChange={setVoucherPage}
              onApprove={handleApproveVoucher}
              onReject={(v) => setRejectTarget(v)}
              onCancel={(v) => setCancelTarget(v)}
              onDelete={handleDeleteVoucher}
              onViewDetail={(v) => setDetailVoucherId(v.id)}
              canCancel={user?.role === 'ADMIN'}
            />
          </Col>
        </Row>
      </div>

      {/* Modals */}
      <VoucherDetailModal
        voucherId={detailVoucherId}
        show={!!detailVoucherId}
        onHide={() => setDetailVoucherId(null)}
      />

      <RejectVoucherModal
        show={!!rejectTarget}
        voucher={rejectTarget}
        onConfirm={handleRejectConfirm}
        onHide={() => setRejectTarget(null)}
        loading={rejectLoading}
      />

      <CancelVoucherModal
        show={!!cancelTarget}
        voucher={cancelTarget}
        onConfirm={handleCancelConfirm}
        onHide={() => setCancelTarget(null)}
        loading={cancelLoading}
      />

      <ForceCheckoutModal
        show={!!forceTarget}
        shift={forceTarget}
        onConfirm={handleForceCheckoutConfirm}
        onHide={() => setForceTarget(null)}
        loading={forceLoading}
      />

      <CashierReportModal
        show={reportModal}
        shift={reportShift}
        report={cashierReport}
        loading={reportLoading}
        onHide={() => setReportModal(false)}
      />

      {/* #110729 — phiếu chi lùi ca (chỉ Admin). */}
      <BackdatedVoucherModal
        show={backdatedOpen}
        branchId={branchId}
        branchLabel={selectedBranch?.name}
        onConfirm={handleCreateBackdated}
        onHide={() => setBackdatedOpen(false)}
        loading={backdatedLoading}
      />
    </PageLayout>
  );
};

export default CashierManagementPage;
